<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({ provider: { type: String, required: true } });
const emit = defineEmits(['close']);
const { t } = useI18n();
const route = useRoute();
const saving = ref(false);
const form = ref({
  environment: 'production',
  client_id: '',
  client_secret: '',
  api_key: '',
  account_id: '',
  hottok: '',
  webhook_secret: '',
  webhook_token: '',
});

const fields = computed(() => {
  if (props.provider === 'mercado_pago')
    return ['client_id', 'client_secret', 'webhook_secret'];
  if (props.provider === 'asaas') return ['api_key', 'webhook_token'];
  if (props.provider === 'kiwify')
    return ['client_id', 'client_secret', 'account_id'];
  return ['client_id', 'client_secret', 'hottok'];
});

const save = async () => {
  saving.value = true;
  try {
    const credentials = fields.value.reduce(
      (result, field) => ({ ...result, [field]: form.value[field] }),
      {}
    );
    const endpoint = `/api/v1/accounts/${route.params.accountId}/sales/connections/${props.provider}`;
    if (props.provider === 'mercado_pago') {
      const response = await window.axios.post(`${endpoint}/authorize`, {
        settings: { environment: form.value.environment },
        credentials,
      });
      window.location.assign(response.data.authorization_url);
      return;
    }
    await window.axios.post(endpoint, {
      settings:
        props.provider === 'kiwify'
          ? {}
          : { environment: form.value.environment },
      credentials,
    });
    useAlert(t('SALES.INTEGRATION_SAVED'));
    emit('close');
  } catch (error) {
    useAlert(error.response?.data?.error || t('SALES.INTEGRATION_ERROR'));
  } finally {
    saving.value = false;
  }
};
</script>

<template>
  <form class="grid w-full gap-4 p-6" @submit.prevent="save">
    <h2 class="text-lg font-semibold">
      {{ t('SALES.CONNECT_PROVIDER', { provider }) }}
    </h2>
    <label v-if="provider !== 'kiwify'" class="grid gap-1 text-sm">
      {{ t('SALES.ENVIRONMENT') }}
      <select
        v-model="form.environment"
        class="rounded border border-n-weak p-2"
      >
        <option value="production">{{ t('SALES.PRODUCTION') }}</option>
        <option value="sandbox">{{ t('SALES.SANDBOX') }}</option>
      </select>
    </label>
    <label v-for="field in fields" :key="field" class="grid gap-1 text-sm">
      {{ t(`SALES.CREDENTIALS.${field}`) }}
      <input
        v-model="form[field]"
        type="password"
        required
        class="rounded border border-n-weak p-2"
        autocomplete="off"
      />
    </label>
    <div class="flex justify-end gap-2">
      <Button
        type="button"
        faded
        :label="t('SALES.CANCEL')"
        @click="emit('close')"
      />
      <Button type="submit" :label="t('SALES.CONNECT')" :is-loading="saving" />
    </div>
  </form>
</template>
